/**
 * Article barcode: an optional identifier, unique per tenant, read by a
 * scanner or printed on a supplier document.
 *
 * A numeric value of 8, 12 or 13 digits is an EAN-8, UPC-A or EAN-13 and
 * must carry its GS1 check digit. Any other value is free text up to
 * `ARTICLE_BARCODE_MAX_LENGTH` characters.
 */
/** Longest barcode an article may carry. */
export const ARTICLE_BARCODE_MAX_LENGTH = 64;
/** GTIN kinds checked by their check digit, by digit count. */
const GTIN_KIND_BY_LENGTH = {
    8: "EAN-8",
    12: "UPC-A",
    13: "EAN-13",
};
const CONTROL_CHARACTER = /\p{Cc}/u;
/** The stored form of a barcode: trimmed, null when blank or absent. */
export function normalizeArticleBarcode(value) {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? null : trimmed;
}
/**
 * GS1 mod-10 check digit of a GTIN body (every digit but the last):
 * from the right, digits weigh 3, 1, 3, 1…
 */
export function gtinCheckDigit(body) {
    let sum = 0;
    for (let i = 0; i < body.length; i += 1) {
        const digit = Number(body[body.length - 1 - i]);
        sum += digit * (i % 2 === 0 ? 3 : 1);
    }
    return (10 - (sum % 10)) % 10;
}
/** Whether `value` is an EAN-8, UPC-A or EAN-13 whose check digit is right. */
export function isValidGtin(value) {
    if (!GTIN_KIND_BY_LENGTH[value.length] || !/^\d+$/.test(value))
        return false;
    return Number(value.at(-1)) === gtinCheckDigit(value.slice(0, -1));
}
/**
 * The key two barcodes are compared by: equal keys designate one article.
 *
 * A UPC-A is the EAN-13 of the same symbol without its leading 0, and
 * scanners send either form, so a valid UPC-A keys as that EAN-13. Every
 * other value — EAN-8, EAN-13, a GTIN-length number whose check digit is
 * wrong, free text — keys as its stored form, so it matches exactly itself.
 */
export function articleBarcodeKey(value) {
    const barcode = normalizeArticleBarcode(value);
    if (barcode === null)
        return null;
    return barcode.length === 12 && isValidGtin(barcode) ? `0${barcode}` : barcode;
}
/** Whether two barcodes designate the same article; never for a blank one. */
export function sameArticleBarcode(a, b) {
    const key = articleBarcodeKey(a);
    return key !== null && key === articleBarcodeKey(b);
}
/**
 * The stored forms an article read as `value` may carry: the value itself
 * first, then, for a valid UPC-A or an EAN-13 starting with 0, the other
 * form of the same symbol. Empty for a blank value.
 */
export function articleBarcodeVariants(value) {
    const barcode = normalizeArticleBarcode(value);
    if (barcode === null)
        return [];
    const key = articleBarcodeKey(barcode);
    if (key === null || key.length !== 13 || !key.startsWith("0") || !isValidGtin(key)) {
        return [barcode];
    }
    const upcA = key.slice(1);
    return barcode === key ? [key, upcA] : [barcode, key];
}
/** Why a barcode is refused, in French; null when it is valid or absent. */
export function articleBarcodeError(value) {
    const barcode = normalizeArticleBarcode(value);
    if (barcode === null)
        return null;
    if (barcode.length > ARTICLE_BARCODE_MAX_LENGTH) {
        return `Le code-barres dépasse ${ARTICLE_BARCODE_MAX_LENGTH} caractères`;
    }
    if (CONTROL_CHARACTER.test(barcode)) {
        return "Le code-barres contient un caractère de contrôle";
    }
    const kind = GTIN_KIND_BY_LENGTH[barcode.length];
    if (kind && /^\d+$/.test(barcode)) {
        const expected = gtinCheckDigit(barcode.slice(0, -1));
        if (Number(barcode.at(-1)) !== expected) {
            return `Code-barres ${kind} invalide (clé de contrôle attendue : ${expected})`;
        }
    }
    return null;
}
//# sourceMappingURL=barcode.js.map